/**
 * User Repository Implementation (Infrastructure)
 *
 * Implements the domain repository interface.
 */

import { AppException, ErrorCode } from '@/core/exceptions';

import type { UserRepository } from '../../domain/repositories';
import type { User, UserResponse, TestCallResponse, ClearCacheResponse } from '../../domain/types';
import type { UserDatasource } from '../datasources';
import { userDatasource } from '../datasources';

// ============================================================================
// Repository Implementation
// ============================================================================

export class UserRepositoryImpl implements UserRepository {
  constructor(
    private readonly datasource: UserDatasource = userDatasource
  ) {}

  async getCurrentUser(): Promise<UserResponse> {
    try {
      return await this.datasource.getCurrentUser();
    } catch (error) {
      throw this.handleError(error, 'Failed to get user profile');
    }
  }

  async updateProfile(data: Partial<User>): Promise<UserResponse> {
    try {
      return await this.datasource.updateProfile(data);
    } catch (error) {
      throw this.handleError(error, 'Failed to update profile');
    }
  }

  async createTestCall(): Promise<TestCallResponse> {
    try {
      return await this.datasource.createTestCall();
    } catch (error) {
      throw this.handleError(error, 'Failed to create test call');
    }
  }

  async endTestCall(channelId: string): Promise<void> {
    try {
      await this.datasource.endTestCall(channelId);
    } catch (error) {
      throw this.handleError(error, 'Failed to end test call');
    }
  }

  async clearUserCache(): Promise<ClearCacheResponse> {
    try {
      return await this.datasource.clearUserCache();
    } catch (error) {
      throw this.handleError(error, 'Failed to clear cache');
    }
  }

  /**
   * Transform errors to domain exceptions
   */
  private handleError(error: unknown, fallbackMessage: string): AppException {
    if (error instanceof AppException) {
      return error;
    }

    const message = error instanceof Error ? error.message : fallbackMessage;

    return new AppException({
      code: ErrorCode.UNKNOWN_ERROR,
      message,
      userMessage: message,
    });
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const userRepository: UserRepository = new UserRepositoryImpl();

